import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppSelector } from 'store/hooks';
import { selectUser } from 'reducers/selectors';
import { TierPeriod, } from '../entities';
import { getItemSKUForProductId, getLastPurchaseFromPurchaseHistory, } from '../iap';
import { selectIsOnSameStore } from '../selectors';
import useStripePayment from './useStripePayment';
const hasPeriodForTier = (period, tier, subscriptions) => {
    const hasYearly = subscriptions.find((sub) => sub.period === period && sub.tier === tier) !==
        undefined;
    return hasYearly ? period : undefined;
};
const canSwitchPeriod = (currentProductId, tier, subscriptions) => {
    const currentPeriod = getItemSKUForProductId(currentProductId)?.period;
    if (currentPeriod === TierPeriod.monthly) {
        return hasPeriodForTier(TierPeriod.yearly, tier, subscriptions);
    }
    if (currentPeriod === TierPeriod.yearly) {
        return hasPeriodForTier(TierPeriod.monthly, tier, subscriptions);
    }
    return undefined;
};
// Indicates whether the user can upgrade each tier from Monthly -> Yearly
const useCanChangeSubscriptionPeriod = (subscriptions, tiersInOrder) => {
    const isOnSameStore = useAppSelector(selectIsOnSameStore);
    const user = useAppSelector(selectUser);
    const { canUseStripe, stripePriceMap } = useStripePayment();
    const [lastPurchaseSKU, setLastPurchaseSKU] = useState(undefined);
    useEffect(() => {
        if (!canUseStripe) {
            getLastPurchaseFromPurchaseHistory().then((itemSku) => {
                setLastPurchaseSKU(itemSku || false);
            });
        }
    }, [canUseStripe]);
    const periodMap = useMemo(() => {
        if (canUseStripe) {
            const { productId } = user.emmaProStatus;
            const { autoRenewProductId } = user.emmaProStatus;
            const currentProductId = user.emmaProStatus.autoRenewing && autoRenewProductId
                ? autoRenewProductId
                : productId;
            const periodInfoMap = {};
            if (!stripePriceMap)
                return periodInfoMap;
            Object.values(stripePriceMap.MONTHLY).forEach((stripeSubscription) => {
                if (stripeSubscription.id === currentProductId) {
                    periodInfoMap[stripeSubscription.tier] = TierPeriod.yearly;
                }
            });
            Object.values(stripePriceMap.YEARLY).forEach((stripeSubscription) => {
                if (stripeSubscription.id === currentProductId) {
                    periodInfoMap[stripeSubscription.tier] = TierPeriod.monthly;
                }
            });
            return periodInfoMap;
        }
        const currentProductId = user.emmaProStatus.productId;
        return tiersInOrder.reduce((prev, tier) => {
            if (lastPurchaseSKU !== undefined &&
                user.emmaProStatus.tier === tier &&
                currentProductId &&
                isOnSameStore) {
                const canSwitchTo = canSwitchPeriod(currentProductId, tier, subscriptions);
                if (lastPurchaseSKU === false ||
                    canSwitchTo !== lastPurchaseSKU?.period) {
                    return {
                        ...prev,
                        [tier]: canSwitchPeriod(currentProductId, tier, subscriptions),
                    };
                }
            }
            return {
                ...prev,
                [tier]: undefined,
            };
        }, {});
    }, [
        canUseStripe,
        user.emmaProStatus,
        tiersInOrder,
        stripePriceMap,
        lastPurchaseSKU,
        isOnSameStore,
        subscriptions,
    ]);
    const onChangedSubscriptionPeriod = useCallback((productId) => {
        if (canUseStripe)
            return;
        const itemSku = getItemSKUForProductId(productId);
        if (itemSku) {
            setLastPurchaseSKU(itemSku);
        }
    }, [canUseStripe]);
    return {
        periodMap,
        onChangedSubscriptionPeriod,
        isFetchingPeriodMap: lastPurchaseSKU === undefined,
    };
};
export default useCanChangeSubscriptionPeriod;
